import { useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Package, ShoppingBag, Tag, Users, Settings, LogOut, ChevronLeft, Image, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import OrderNotificationBadge from '@/components/admin/OrderNotificationBadge';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/products', label: 'Products', icon: Package },
  { to: '/admin/orders', label: 'Orders', icon: ShoppingBag },
  { to: '/admin/coupons', label: 'Coupons', icon: Tag },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/hero-settings', label: 'Hero Section', icon: Image },
  { to: '/admin/promo-slides', label: 'Promo Slides', icon: Layers },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

const AdminLayout = () => {
  const { user, isAdmin, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        // Not logged in - redirect to auth page
        toast({
          title: 'Authentication required',
          description: 'Please log in to access the admin panel.',
          variant: 'destructive'
        });
        navigate('/auth', { replace: true });
      } else if (!isAdmin) {
        // Logged in but not admin - redirect to home
        toast({
          title: 'Access denied',
          description: 'Only authorized admin can access this panel.',
          variant: 'destructive'
        });
        navigate('/auth', { replace: true });
      }
    }
  }, [user, isAdmin, loading, navigate, toast]);

  const handleLogout = async () => {
    try {
      await signOut();
      toast({
        title: 'Logged out successfully',
        description: 'You have been signed out.'
      });
      navigate('/auth', { replace: true });
    } catch (error) {
      toast({
        title: 'Logout error',
        description: 'Failed to log out. Please try again.',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><Skeleton className="h-12 w-48" /></div>;
  }

  if (!user || !isAdmin) return null;

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-64 bg-primary text-primary-foreground flex flex-col shrink-0">
        <div className="p-4 border-b border-primary-foreground/10">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg overflow-hidden bg-white flex items-center justify-center">
              <img 
                src="/logo.png" 
                alt="Debnath Telecom Logo" 
                className="h-7 w-7 object-contain"
              />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-bold text-lg">Admin Panel</h2>
            </div>
            <OrderNotificationBadge />
          </div>
          <p className="text-xs opacity-60">Debnath Telecom</p>
          {user?.email && (
            <p className="text-xs opacity-50 mt-2 truncate" title={user.email}>
              {user.email}
            </p>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(item => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active ? 'bg-primary-foreground/15 font-medium' : 'opacity-70 hover:opacity-100 hover:bg-primary-foreground/5'
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-primary-foreground/10 space-y-2">
          <Link to="/">
            <Button variant="ghost" size="sm" className="w-full justify-start text-primary-foreground opacity-70 hover:opacity-100">
              <ChevronLeft className="h-4 w-4 mr-2" /> Back to Store
            </Button>
          </Link>
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-primary-foreground opacity-70 hover:opacity-100" 
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 bg-muted/30 p-6 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
